import React from "react";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Shield,
  AlertOctagon,
  Info,
  Sparkles,
  Activity,
  Bell,
  ChevronRight,
} from "lucide-react";
import type { EmailAnalysisResult } from "../../services/EmailHygieneService";
import {
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from "recharts";

interface EmailHygieneResultsProps {
  result: EmailAnalysisResult;
  onReset: () => void;
  onOpenMonitoring: () => void;
}

const EmailHygieneResults: React.FC<EmailHygieneResultsProps> = ({
  result,
  onReset,
  onOpenMonitoring,
}) => {
  const { originalData } = result;
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-dark";
    if (score >= 60) return "text-yellow-DEFAULT";
    return "text-red-dark";
  };

  const statusDistribution = [
    {
      name: "Success",
      value: Object.values(originalData.summary).filter(
        (s) => s.status === "success"
      ).length,
    },
    {
      name: "Warning",
      value: Object.values(originalData.summary).filter(
        (s) => s.status === "warning"
      ).length,
    },
    {
      name: "Error",
      value: Object.values(originalData.summary).filter(
        (s) => s.status === "error"
      ).length,
    },
    {
      name: "Info",
      value: Object.values(originalData.summary).filter(
        (s) => s.status === "info"
      ).length,
    },
  ];

  const securityScores = [
    { name: "SPF", score: originalData.summary.spf.score },
    { name: "DKIM", score: originalData.summary.dkim.score },
    { name: "DMARC", score: originalData.summary.dmarc.score },
    { name: "MX", score: originalData.summary.mx.score },
    { name: "SSL", score: originalData.summary.ssl.score },
    { name: "Blacklist", score: originalData.summary.blacklist.score },
  ];

  return (
    <div className="min-h-screen bg-background-default dark:bg-background-dark">
      <header className="relative min-h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-yellow-DEFAULT/10 via-transparent to-secondary/5" />
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.07]"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25px 25px, currentColor 2px, transparent 0),
                linear-gradient(to right, currentColor 1px, transparent 1px),
                linear-gradient(to bottom, currentColor 1px, transparent 1px)
              `,
              backgroundSize: "50px 50px",
              maskImage:
                "radial-gradient(circle at center, black, transparent 80%)",
            }}
          />

          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-yellow-DEFAULT/10 rounded-full blur-3xl animate-gentle-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-gentle-pulse delay-1000" />

          <div className="absolute top-1/3 w-full h-px bg-gradient-to-r from-transparent via-yellow-DEFAULT/20 to-transparent" />
          <div className="absolute bottom-1/3 w-full h-px bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={onReset}
              className="flex items-center gap-2 px-4 py-2 text-text-secondary hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>New Analysis</span>
            </button>
            <div className="h-6 w-px bg-divider dark:bg-divider-dark" />
            <span className="text-sm text-text-secondary">
              Analysis completed{" "}
              {new Date(originalData.timestamp).toLocaleString()}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-3xl font-bold mb-3">{originalData.domain}</h1>
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    originalData.deliverabilityStatus === "Critical"
                      ? "bg-red-dark/10 text-red-dark"
                      : originalData.deliverabilityStatus === "Warning"
                      ? "bg-yellow-DEFAULT/10 text-yellow-DEFAULT"
                      : "bg-green-dark/10 text-green-dark"
                  }`}
                >
                  {originalData.deliverabilityStatus}
                </span>
                <span className="text-text-secondary">
                  {
                    Object.values(originalData.summary).filter(
                      (s) => s.status === "success"
                    ).length
                  }{" "}
                  checks passed
                </span>
              </div>
            </div>

            <div className="flex justify-center md:justify-end">
              <div className="text-center">
                <div className="text-[200px] font-black leading-none tracking-tighter">
                  <span className={getScoreColor(originalData.overallScore)}>
                    {originalData.overallScore}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Sparkles className="w-4 h-4 text-yellow-DEFAULT" />
                  <span className="text-lg text-text-secondary">
                    Overall Health Score
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="relative max-w-7xl mx-auto px-6 py-12 space-y-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-dark" />
              <h3 className="font-semibold text-red-dark">Critical Issues</h3>
            </div>
            {originalData.issuesSummary.critical.map((issue, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-4 bg-red-dark/5 rounded-lg"
              >
                <AlertOctagon className="w-4 h-4 text-red-dark flex-shrink-0 mt-0.5" />
                <span className="text-text-secondary">{issue}</span>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-DEFAULT" />
              <h3 className="font-semibold text-yellow-DEFAULT">Warnings</h3>
            </div>
            {originalData.issuesSummary.warning.map((warning, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-4 bg-yellow-DEFAULT/5 rounded-lg"
              >
                <AlertTriangle className="w-4 h-4 text-yellow-DEFAULT flex-shrink-0 mt-0.5" />
                <span className="text-text-secondary">{warning}</span>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-dark" />
              <h3 className="font-semibold text-green-dark">Recommendations</h3>
            </div>
            {originalData.issuesSummary.recommendations.map((rec, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-4 bg-green-dark/5 rounded-lg"
              >
                <CheckCircle className="w-4 h-4 text-green-dark flex-shrink-0 mt-0.5" />
                <span className="text-text-secondary">{rec}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-background-paper dark:bg-background-paperDark rounded-xl overflow-hidden">
            <div className="px-6 pt-6">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-5 h-5 text-yellow-DEFAULT" />
                <h3 className="text-lg font-semibold">Security Scores</h3>
              </div>
            </div>
            <div className="h-64 px-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={securityScores} barSize={24}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-divider dark:stroke-divider-dark"
                    opacity={0.5}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    className="text-text-secondary dark:text-text-secondaryDark"
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                  />
                  <YAxis
                    domain={[0, 100]}
                    className="text-text-secondary dark:text-text-secondaryDark"
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                  />
                  <Tooltip
                    cursor={{ fill: "var(--background-panel)" }}
                    contentStyle={{
                      backgroundColor: "var(--background-paper)",
                      borderColor: "var(--divider)",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar
                    dataKey="score"
                    radius={[4, 4, 4, 4]}
                    fill="var(--yellow-DEFAULT)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-background-paper dark:bg-background-paperDark rounded-xl overflow-hidden">
            <div className="px-6 pt-6">
              <div className="flex items-center gap-2 mb-6">
                <Activity className="w-5 h-5 text-yellow-DEFAULT" />
                <h3 className="text-lg font-semibold">
                  Check Status Distribution
                </h3>
              </div>
            </div>
            <div className="px-6 pb-6">
              <div className="space-y-4">
                {statusDistribution.map((status) => (
                  <div key={status.name} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary dark:text-text-secondaryDark">
                        {status.name}
                      </span>
                      <span className="font-medium">{status.value}</span>
                    </div>
                    <div className="h-2 w-full bg-background-panel dark:bg-background-panelDark rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          status.name === "Success"
                            ? "bg-green-dark"
                            : status.name === "Warning"
                            ? "bg-yellow-DEFAULT"
                            : status.name === "Error"
                            ? "bg-red-dark"
                            : "bg-text-secondary"
                        }`}
                        style={{
                          width: `${
                            (status.value /
                              Object.values(originalData.summary).length) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-yellow-DEFAULT" />
            <h2 className="text-2xl font-bold">AI Analysis</h2>
          </div>
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <div className="text-text-secondary dark:text-text-secondaryDark whitespace-pre-wrap">
              {result.aiAnalysis}
            </div>
          </div>
        </div>

        <div className="mt-8 bg-yellow-DEFAULT/5 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-yellow-DEFAULT" />
              <div>
                <h3 className="font-semibold">Set Up Monitoring</h3>
                <p className="text-text-secondary">
                  Keep track of your email infrastructure health with automated
                  checks
                </p>
              </div>
            </div>
            <button
              onClick={onOpenMonitoring}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-DEFAULT text-white rounded-lg hover:bg-yellow-DEFAULT/90 transition-colors"
            >
              <span>Configure</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmailHygieneResults;
